import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, MessageCircle, Clock, ShieldCheck, MapPin, Send,
  Truck, Sparkles, Calendar, BookOpen,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import { TrustSection, IconCardGrid } from "../components/TrustLayout";

/**
 * /contact — upgraded to the Phase-A trust system. Sub-topic cards for
 * delivery, product, subscription and assessment support; calmer tone,
 * "we reply personally" copy, response-time strip, plus the existing
 * email/WhatsApp/order cards and message form.
 */
export default function ContactPage() {
  const navigate = useNavigate();
  const [whatsapp, setWhatsapp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("General question");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useSeo({
    title: "Contact Feel Greats — real humans, personal replies",
    description:
      "Real human support for delivery, product, subscription and assessment questions. WhatsApp, email, or our message form — replies within hours, never bots.",
    canonical: `${PROD_DOMAIN}/contact`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Feel Greats",
        "url": `${PROD_DOMAIN}/contact`,
        "mainEntity": {
          "@type": "Organization",
          "name": "Feel Greats",
          "url": PROD_DOMAIN,
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "support@feelgreats.co.uk",
              "availableLanguage": ["en"],
              "areaServed": "Worldwide",
            },
            {
              "@type": "ContactPoint",
              "contactType": "wellness mentor (WhatsApp)",
              "telephone": "+44-7424-225333",
              "availableLanguage": ["en"],
              "areaServed": "Worldwide",
              "hoursAvailable": "Mo-Fr 09:00-18:00 GMT",
            },
          ],
        },
      },
    ],
  });

  useEffect(() => {
    api.get("/config").then(({ data }) => setWhatsapp(data?.whatsapp_number || "")).catch(() => {});
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    const subject = `[${topic}] from ${name || "Feel Greats visitor"}`;
    const body = `Hi Feel Greats team,\n\n${message}\n\n— ${name}\n${email}`;
    const mailto = `mailto:support@feelgreats.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setTimeout(() => {
      setSending(false);
      toast.success("Opening your email app to send this message…");
    }, 500);
  };

  const waNumber = whatsapp || "447424225333";
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Feel Greats team! I have a question.")}`;

  return (
    <main className="bg-gradient-to-b from-emerald-50/30 via-white to-white overflow-x-hidden min-h-[calc(100vh-4rem)]" data-testid="contact-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 sm:pt-20 sm:pb-12 text-center">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700 bg-emerald-50/60 rounded-full px-3 py-1 border border-emerald-100">
          <Sparkles className="w-3 h-3" /> Real humans, personal replies
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-emerald-950 leading-[1.05] mt-5">
          We <em className="italic text-emerald-700">reply</em>. Personally.
        </h1>
        <p className="text-lg text-emerald-900/75 mt-5 max-w-xl mx-auto leading-relaxed">
          Every email and WhatsApp message gets a thoughtful reply from a named human on our wellness team. Never a chatbot. Never a ticket queue. Whether it's a quick question or your assessment results, no question is too small.
        </p>
      </section>

      {/* Three top contact paths */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="contact-top-paths">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ContactCard
            icon={Mail}
            title="Email support"
            body="Best for longer questions, ingredient deep-dives, or anything you'd rather think through in writing."
            cta={{
              label: "support@feelgreats.co.uk",
              href: "mailto:support@feelgreats.co.uk",
              testid: "contact-email-cta",
            }}
            sub="We reply within 1 business day · Mon–Fri"
          />
          <ContactCard
            icon={MessageCircle}
            title="WhatsApp wellness coach"
            body="Quick wellness guidance, assessment questions, or live nudges before you buy. Real-time during UK hours."
            cta={{
              label: "Open WhatsApp",
              href: waUrl,
              external: true,
              testid: "contact-whatsapp-cta",
            }}
            sub="Live · 09:00–18:00 GMT · usually within 1–4 hours"
            highlight
          />
          <ContactCard
            icon={ShieldCheck}
            title="Order & refund help"
            body="Already a customer? Send your order ID and we'll resolve it on the spot — usually within 24 hours."
            cta={{
              label: "support@feelgreats.co.uk",
              href: "mailto:support@feelgreats.co.uk?subject=Order%20question",
              testid: "contact-order-cta",
            }}
            sub="See our Refund Policy for full details"
          />
        </div>
      </section>

      {/* Topic-specific support */}
      <TrustSection
        eyebrow="What can we help with?"
        title="Four common reasons people message us."
        body="Pick the closest fit and we'll route you to the right team member. Or just message us — we'll figure out the rest."
        tone="white"
        testid="contact-topics"
      >
        <IconCardGrid
          cols={4}
          cards={[
            {
              icon: Truck,
              eyebrow: "Delivery support",
              title: "Where's my parcel?",
              body: "Tracking, delays, missing items, or address changes. Have your order ID handy.",
              cta: { label: "Email delivery team", to: "/contact?topic=delivery", testid: "contact-topic-delivery" },
            },
            {
              icon: BookOpen,
              eyebrow: "Product questions",
              title: "Ingredients & usage",
              body: "Is this product right for me? Does it interact with my medication? What does the PDF say?",
              cta: { label: "Ask a mentor", to: "/mentor-support", testid: "contact-topic-product" },
            },
            {
              icon: Calendar,
              eyebrow: "Subscription help",
              title: "Pause, skip or cancel",
              body: "Adjusting your subscription, payment methods, or moving to a one-time order.",
              cta: { label: "Subscription help", to: "/contact?topic=subscription", testid: "contact-topic-subscription" },
            },
            {
              icon: Sparkles,
              eyebrow: "Assessment help",
              title: "Your wellness report",
              body: "Need to retake the assessment, or want a mentor to walk through your report with you?",
              cta: { label: "Talk to a mentor", to: "/mentor-support", testid: "contact-topic-assessment" },
            },
          ]}
        />
      </TrustSection>

      {/* Message form */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="contact-form-section">
        <Card className="rounded-3xl border-emerald-100" data-testid="contact-form-card">
          <CardContent className="p-6 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Or use the form</p>
            <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light mt-1">Send us a message</h2>
            <p className="text-sm text-emerald-900/70 mt-2 max-w-2xl">
              Prefer a form? Fill in the details below and we'll open your email app with a ready-to-send message. Same human reply on the other side.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4 mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cn">Your name</Label>
                  <Input
                    id="cn"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200"
                    placeholder="Jane Doe"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="ce">Your email</Label>
                  <Input
                    id="ce"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200"
                    placeholder="you@example.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ct">Topic</Label>
                <select
                  id="ct"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-emerald-200 px-3 py-2 bg-white text-emerald-900"
                  data-testid="contact-topic"
                >
                  <option>General question</option>
                  <option>Health assessment</option>
                  <option>Product / ingredients</option>
                  <option>Delivery / order</option>
                  <option>Subscription / billing</option>
                  <option>Refund request</option>
                  <option>Press / partnerships</option>
                  <option>Bug report</option>
                </select>
              </div>
              <div>
                <Label htmlFor="cm">Message</Label>
                <Textarea
                  id="cm"
                  required
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-1 rounded-xl border-emerald-200"
                  placeholder="Tell us how we can help — no question is too small."
                  data-testid="contact-message"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="submit"
                  disabled={sending}
                  className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-5"
                  data-testid="contact-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Opening email…" : "Send message"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50"
                  data-testid="contact-back"
                >
                  Back to home
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </section>

      {/* Info tiles */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoTile icon={Clock} title="Reply times" body="Most messages answered within 1–4 working hours, always within 1 business day. WhatsApp is fastest." />
          <InfoTile icon={MapPin} title="Where we operate" body="Feel Greats is operated from the United Kingdom and serves members worldwide. UK + USA storefronts; international by request." />
        </div>
      </section>
    </main>
  );
}

function ContactCard({ icon: Icon, title, body, cta, sub, highlight }) {
  return (
    <Card
      className={`rounded-3xl ${highlight ? "border-amber-300 ring-2 ring-amber-200/60 bg-amber-50/30" : "border-emerald-100 bg-white"}`}
    >
      <CardContent className="p-6 sm:p-7">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 mb-4">
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </span>
        <h3 className="font-display text-lg text-emerald-950 font-medium">{title}</h3>
        <p className="text-sm text-emerald-900/70 mt-2 leading-relaxed">{body}</p>
        <a
          href={cta.href}
          {...(cta.external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
          className="inline-block mt-4 text-emerald-700 font-semibold hover:underline break-all"
          data-testid={cta.testid}
        >
          {cta.label}
        </a>
        {sub ? <div className="text-xs text-emerald-900/60 mt-3">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

function InfoTile({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-5 flex items-start gap-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="font-medium text-emerald-950 text-sm">{title}</div>
        <div className="text-xs text-emerald-900/70 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
